import { z } from "zod";
import { ufSchema } from "./calculation.js";
import { environmentSchema } from "./context.js";
/** Closed, declared mapping only. This API never opens or verifies the parent XLSX. */
export const fiscalExtractSheetSchema = z.enum([
    "Parâmetros Compra",
    "Parâmetros Compra (2)",
    "Parâmetros Venda",
    "Parâmetros Venda (2)",
    "Auditoria NCM",
    "Auditoria NCM (2)",
]);
const sha = z.string().regex(/^[a-f0-9]{64}$/);
export const FISCAL_EXTRACT_MAX_BYTES = 256_000;
export const fiscalExtractLiteralSchema = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("NCM"),
        value: z.string().regex(/^(?:\d{4}|\d{6}|\d{8}|\d{4}\.\d{2}(?:\.\d{2})?)$/),
    })
        .strict(),
    z
        .object({
        kind: z.literal("NCM_VARIANT"),
        ncm: z.string().regex(/^\d{8}$/),
        variantSha256: sha.nullable(),
    })
        .strict(),
    z
        .object({ kind: z.literal("CEST"), value: z.string().regex(/^(?:\d{7}|\d{2}\.\d{3}\.\d{2})$/) })
        .strict(),
    z.object({ kind: z.literal("UF"), value: ufSchema }).strict(),
    z
        .object({
        kind: z.literal("DECIMAL"),
        raw: z
            .string()
            .max(64)
            .regex(/^[+-]?\d+(?:\.\d+)?(?:[eE][+-]?\d{1,3})?$/),
        unit: z.enum(["EXCEL_FRACTION", "PERCENT", "UNKNOWN"]),
    })
        .strict(),
    z.object({ kind: z.literal("BOOLEAN"), value: z.boolean() }).strict(),
    z.object({ kind: z.literal("HASH_ONLY"), contentSha256: sha }).strict(),
]);
export const fiscalExtractCellSchema = z
    .object({
    column: z.string().regex(/^[A-M]$/),
    observation: z.discriminatedUnion("kind", [
        ...fiscalExtractLiteralSchema.options,
        z.object({ kind: z.literal("MISSING") }).strict(),
        z
            .object({
            kind: z.literal("FORMULA"),
            formulaSha256: sha,
            cache: z.discriminatedUnion("state", [
                z.object({ state: z.literal("ABSENT") }).strict(),
                z.object({ state: z.literal("EMPTY") }).strict(),
                z.object({ state: z.literal("VALUE"), value: fiscalExtractLiteralSchema }).strict(),
            ]),
        })
            .strict(),
    ]),
})
    .strict();
const fields = (declarations) => Object.fromEntries(Object.entries(declarations).map(([column, [field, kind]]) => [column, { field, kind }]));
export const FISCAL_EXTRACT_BLOCKS = {
    PURCHASE_SCALARS: {
        sheet: "Parâmetros Compra",
        firstRow: 5,
        lastRow: 8,
        referenceOnly: false,
        fields: fields({ A: ["sourceLabel", "HASH_ONLY"], B: ["fiscalScalar", "DECIMAL"] }),
    },
    PURCHASE_ST: {
        sheet: "Parâmetros Compra",
        firstRow: 11,
        lastRow: 24,
        referenceOnly: false,
        fields: fields({
            A: ["sourceKey", "HASH_ONLY"],
            B: ["originUf", "UF"],
            C: ["ncmVariant", "NCM_VARIANT"],
            D: ["ncmScope", "NCM"],
            E: ["cest", "CEST"],
            F: ["originalMva", "DECIMAL"],
            G: ["destinationInternalRate", "DECIMAL"],
            H: ["appliedMvaObservation", "DECIMAL"],
            I: ["treatmentText", "HASH_ONLY"],
            J: ["legalReferenceText", "HASH_ONLY"],
        }),
    },
    COLLECTION: {
        sheet: "Parâmetros Compra",
        firstRow: 29,
        lastRow: 34,
        referenceOnly: false,
        fields: fields({
            A: ["scenarioText", "HASH_ONLY"],
            B: ["collectorText", "HASH_ONLY"],
            C: ["collectionDocumentText", "HASH_ONLY"],
            D: ["applicabilityText", "HASH_ONLY"],
            E: ["conditionText1", "HASH_ONLY"],
            F: ["conditionText2", "HASH_ONLY"],
            G: ["authorStatusText", "HASH_ONLY"],
            H: ["sourceReferenceText", "HASH_ONLY"],
        }),
    },
    SALE_SCALARS: {
        sheet: "Parâmetros Venda",
        firstRow: 6,
        lastRow: 10,
        referenceOnly: false,
        fields: fields({
            A: ["sourceLabel", "HASH_ONLY"],
            B: ["fiscalScalar", "DECIMAL"],
            C: ["conditionText", "HASH_ONLY"],
        }),
    },
    SALE_ROUTES: {
        sheet: "Parâmetros Venda",
        firstRow: 13,
        lastRow: 66,
        referenceOnly: false,
        fields: fields({
            A: ["sourceKey", "HASH_ONLY"],
            B: ["originUf", "UF"],
            C: ["destinationUf", "UF"],
            D: ["otherRateContextText", "HASH_ONLY"],
            E: ["interstateRate", "DECIMAL"],
            F: ["destinationInternalRate", "DECIMAL"],
            G: ["fcpRate", "DECIMAL"],
            H: ["regimeContextText", "HASH_ONLY"],
            I: ["treatmentText", "HASH_ONLY"],
            J: ["notesText", "HASH_ONLY"],
        }),
    },
    SALE_ST: {
        sheet: "Parâmetros Venda",
        firstRow: 72,
        lastRow: 128,
        referenceOnly: false,
        fields: fields({
            A: ["sourceKey", "HASH_ONLY"],
            B: ["originUf", "UF"],
            C: ["destinationUf", "UF"],
            D: ["ncmVariant", "NCM_VARIANT"],
            E: ["cest", "CEST"],
            F: ["mvaObservation", "DECIMAL"],
            G: ["stApplicabilityDeclaration", "BOOLEAN"],
            H: ["productScopeText", "HASH_ONLY"],
            I: ["legalReferenceText", "HASH_ONLY"],
        }),
    },
    CLASSIFICATION: {
        sheet: "Auditoria NCM",
        firstRow: 5,
        lastRow: 21,
        referenceOnly: false,
        fields: fields({
            A: ["ncm", "NCM"],
            C: ["familyText", "HASH_ONLY"],
            D: ["spStDeclaration", "BOOLEAN"],
            E: ["spCest", "CEST"],
            F: ["spMvaObservation", "DECIMAL"],
            G: ["esStDeclaration", "BOOLEAN"],
            H: ["esCest", "CEST"],
            I: ["esMvaObservation", "DECIMAL"],
            J: ["conditionText", "HASH_ONLY"],
            K: ["authorConfidenceText", "HASH_ONLY"],
            L: ["reviewActionText", "HASH_ONLY"],
            M: ["sourceReferenceText", "HASH_ONLY"],
        }),
    },
    GEOGRAPHIC_REFERENCE: {
        sheet: "Parâmetros Venda",
        firstRow: 13,
        lastRow: 39,
        referenceOnly: true,
        fields: fields({ L: ["uf", "UF"], M: ["stateNameText", "HASH_ONLY"] }),
    },
};
export const fiscalExtractBlockSchema = z.enum([
    "PURCHASE_SCALARS",
    "PURCHASE_ST",
    "COLLECTION",
    "SALE_SCALARS",
    "SALE_ROUTES",
    "SALE_ST",
    "CLASSIFICATION",
    "GEOGRAPHIC_REFERENCE",
]);
export const commercialFiscalExtractSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    profile: z.literal("COMMERCIAL_FISCAL_PARAMETERS_V1"),
    extractorVersion: z.literal("manual-fiscal-json-v1"),
    parentWorkbookSha256: sha,
    /** Field IDs are a declared mapping, not verified source header text. */
    sheets: z
        .array(z
        .object({
        name: fiscalExtractSheetSchema,
        worksheetPart: z.string().regex(/^xl\/worksheets\/sheet[1-9]\d{0,3}\.xml$/),
        headers: z
            .array(z
            .object({
            block: fiscalExtractBlockSchema,
            mapping: z
                .array(z
                .object({
                column: z.string().regex(/^[A-M]$/),
                field: z.enum([
                    ...new Set(Object.values(FISCAL_EXTRACT_BLOCKS).flatMap((b) => Object.values(b.fields).map((f) => f.field))),
                ]),
            })
                .strict())
                .min(2)
                .max(13),
        })
            .strict())
            .min(1)
            .max(8),
    })
        .strict())
        .min(1)
        .max(6),
    rows: z
        .array(z
        .object({
        sheet: fiscalExtractSheetSchema,
        block: fiscalExtractBlockSchema,
        row: z.number().int().positive().max(128),
        cells: z.array(fiscalExtractCellSchema).min(2).max(13),
    })
        .strict())
        .min(1)
        .max(500),
})
    .strict();
export const commercialFiscalExtractRequestSchema = z.discriminatedUnion("mode", [
    z
        .object({
        schemaVersion: z.literal("1.0.0"),
        mode: z.literal("DRY_RUN"),
        extract: commercialFiscalExtractSchema,
    })
        .strict(),
    z
        .object({
        schemaVersion: z.literal("1.0.0"),
        mode: z.literal("COMMIT"),
        extract: commercialFiscalExtractSchema,
        expectedExtractSha256: sha,
        confirmedFiscalSelection: z.literal(true),
    })
        .strict(),
]);
export const fiscalExtractDiagnosticSchema = z.enum([
    "LAYOUT_MAPPING_INVALID",
    "PROFILE_RANGE_INVALID",
    "DUPLICATE_LOCATOR",
    "CELL_TYPE_INVALID",
    "MISSING_VALUE",
    "UNKNOWN_UNIT",
    "FREE_TEXT_NOT_RETAINED",
    "FORMULA_NOT_EXECUTED",
    "CACHE_UNAVAILABLE",
    "ALIAS_CONFLICT",
]);
export const commercialFiscalExtractResultSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    mode: z.enum(["DRY_RUN", "COMMIT"]),
    extractId: z.string().uuid().nullable(),
    createdAt: z.string().datetime().nullable(),
    createdBy: z.string().uuid().nullable(),
    environment: environmentSchema,
    state: z.literal("NEW"),
    confidence: z.null(),
    executable: z.literal(false),
    autoPublished: z.literal(false),
    parentWorkbookSha256: sha,
    parentVerification: z.literal("DECLARED_UNVERIFIED"),
    layoutVerification: z.literal("DECLARED_PROFILE_MAPPING"),
    extractSha256: sha,
    canonicalSnapshot: z.string().max(FISCAL_EXTRACT_MAX_BYTES),
    normalizedText: z.string().max(FISCAL_EXTRACT_MAX_BYTES),
    counts: z
        .object({
        observations: z.number().int().nonnegative(),
        fiscalAssertions: z.number().int().nonnegative().max(157),
        unknownFiscalRows: z.number().int().nonnegative().max(157),
        geographicReferences: z.number().int().nonnegative().max(27),
        duplicateObservations: z.number().int().nonnegative(),
        invalidRows: z.number().int().nonnegative(),
        approvedRules: z.literal(0),
        publishedRules: z.literal(0),
    })
        .strict(),
    rows: z
        .array(z
        .object({
        sheet: fiscalExtractSheetSchema,
        block: fiscalExtractBlockSchema,
        row: z.number().int(),
        locator: z
            .object({
            kind: z.literal("document"),
            value: z.string(),
            textStart: z.number().int().nonnegative(),
            textEnd: z.number().int().nonnegative(),
        })
            .strict(),
        recordSha256: sha,
        diagnostics: z.array(fiscalExtractDiagnosticSchema),
        referenceOnly: z.boolean(),
    })
        .strict())
        .max(500),
    persistable: z.boolean(),
    reused: z.boolean(),
    requiredReview: z.tuple([
        z.literal("SOURCE_PROVENANCE"),
        z.literal("OFFICIAL_EVIDENCE"),
        z.literal("LEGAL_VALIDITY"),
        z.literal("PRODUCT_APPLICABILITY"),
    ]),
    legalValidity: z.literal("UNKNOWN"),
    effectiveFrom: z.null(),
    effectiveTo: z.null(),
})
    .strict();
//# sourceMappingURL=commercial-fiscal-extract.js.map